import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  ArrowLeft, Package, User, Calendar, Clock, CheckCircle, XCircle, 
  AlertTriangle, Save, FileText, Upload, Download, Trash2, Lock, Eye
} from 'lucide-react';
import { toast } from 'sonner';

// Rôles autorisés à voir les documents confidentiels
const ROLES_DOCUMENTS_CONFIDENTIELS = ['Manager', 'HSE', 'RH', 'Admin'];

const DemandeEPIDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [demande, setDemande] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [userId, setUserId] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [updateData, setUpdateData] = useState({
    statut: '',
    commentaire_hse: '',
    date_livraison: ''
  });
  const [documentDescription, setDocumentDescription] = useState('');

  const loadUserRole = useCallback(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserRole(user.role || '');
    setUserId(user.id || '');
  }, []);

  const loadDemande = useCallback(async () => {
    try {
      const response = await api.get(`/demandes-epi/${id}`);
      setDemande(response);
      setUpdateData({
        statut: response.statut,
        commentaire_hse: response.commentaire_hse || '',
        date_livraison: response.date_livraison || ''
      });
    } catch (error) {
      console.error('Error loading demande:', error);
      toast.error('Erreur lors du chargement de la demande');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadDocuments = useCallback(async () => {
    // Vérifier si l'utilisateur a le droit de voir les documents
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!ROLES_DOCUMENTS_CONFIDENTIELS.includes(user.role)) {
      return;
    }
    
    try {
      const response = await api.get(`/demandes-epi/${id}/documents`);
      setDocuments(response.documents || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      // Ne pas afficher d'erreur si accès refusé
      if (error.status !== 403) {
        toast.error('Erreur lors du chargement des documents');
      }
    }
  }, [id]);

  useEffect(() => {
    loadUserRole();
    loadDemande();
    loadDocuments();
  }, [id, loadUserRole, loadDemande, loadDocuments]);

  const handleUpdate = async () => {
    setUpdating(true);
    try {
      await api.put(`/demandes-epi/${id}`, updateData);
      toast.success('Demande mise à jour avec succès');
      setEditMode(false);
      loadDemande();
    } catch (error) {
      console.error('Error updating demande:', error);
      toast.error(error.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier la taille du fichier (max 10 MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10 MB');
      return;
    }

    // Vérifier le type de fichier
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.xls', '.xlsx'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.error(`Type de fichier non autorisé. Extensions autorisées: ${allowedExtensions.join(', ')}`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (documentDescription) {
        formData.append('description', documentDescription);
      }

      const token = localStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${API_URL}/api/demandes-epi/${id}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur lors de l\'upload');
      }

      toast.success('Document uploadé avec succès');
      setDocumentDescription('');
      loadDocuments();
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(error.message || 'Erreur lors de l\'upload du document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const token = localStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${API_URL}/api/demandes-epi/${id}/documents/${documentId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erreur lors du téléchargement du document');
    }
  };

  const handleViewDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('access_token');
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${API_URL}/api/demandes-epi/${id}/documents/${documentId}/view`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la visualisation');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Erreur lors de la visualisation du document');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) {
      return;
    }

    try {
      await api.delete(`/demandes-epi/${id}/documents/${documentId}`);
      toast.success('Document supprimé avec succès');
      loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error(error.detail || 'Erreur lors de la suppression du document');
    }
  };

  const getStatutBadge = (statut) => {
    const config = {
      'En attente': { icon: Clock, color: 'bg-orange-100 text-orange-800', label: 'En attente' },
      'En cours': { icon: Clock, color: 'bg-blue-100 text-blue-800', label: 'En cours' },
      'Livrée': { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Livrée' },
      'Rejetée': { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Rejetée' }
    };

    const { icon: Icon, color, label } = config[statut] || config['En attente'];

    return (
      <Badge className={`${color} gap-1`}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const calculateJoursRestants = (dateLimite) => {
    if (!dateLimite) return null;
    const today = new Date();
    const limite = new Date(dateLimite);
    const diff = Math.ceil((limite - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!demande) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-4" />
        <p className="text-gray-600">Demande non trouvée</p>
      </div>
    );
  }

  const joursRestants = calculateJoursRestants(demande.date_limite_remplacement);
  const isHSE = userRole === 'HSE' || userRole === 'Admin';
  const canViewDocuments = ROLES_DOCUMENTS_CONFIDENTIELS.includes(userRole);
  const canUploadDocuments = ROLES_DOCUMENTS_CONFIDENTIELS.includes(userRole);
  const canDeleteDocument = (doc) => {
    return userRole === 'Admin' || userRole === 'HSE' || doc.uploaded_by_user_id === userId;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-8 w-8 text-blue-600" />
            Détails de la Demande
          </h1>
          <p className="text-gray-600 mt-2">Demande créée le {formatDate(demande.date_demande)}</p>
        </div>
        <div className="flex gap-3">
          {isHSE && !editMode && demande.statut !== 'Rejetée' && (
            <Button onClick={() => setEditMode(true)} variant="outline">
              Modifier le statut
            </Button>
          )}
          <Button onClick={() => navigate('/epi/demandes')} variant="outline" className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          {/* Statut */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Statut de la demande
                {getStatutBadge(demande.statut)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {demande.statut === 'Livrée' && demande.date_limite_remplacement && (
                <div className="space-y-3">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Date de livraison:</strong> {formatDate(demande.date_livraison)}
                    </p>
                    <p className="text-sm text-blue-800">
                      <strong>Date limite de remplacement:</strong> {formatDate(demande.date_limite_remplacement)}
                    </p>
                  </div>
                  {joursRestants !== null && (
                    <div className={`p-3 rounded-lg ${
                      joursRestants < 0 ? 'bg-red-50' : joursRestants <= 30 ? 'bg-orange-50' : 'bg-green-50'
                    }`}>
                      <p className={`text-sm font-medium ${
                        joursRestants < 0 ? 'text-red-800' : joursRestants <= 30 ? 'text-orange-800' : 'text-green-800'
                      }`}>
                        {joursRestants < 0 ? (
                          `❌ EPI expiré depuis ${Math.abs(joursRestants)} jour${Math.abs(joursRestants) > 1 ? 's' : ''}`
                        ) : joursRestants <= 30 ? (
                          `⚠️ À renouveler dans ${joursRestants} jour${joursRestants > 1 ? 's' : ''}`
                        ) : (
                          `✅ Valide encore ${joursRestants} jours`
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Collaborateur */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Collaborateur
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <Label className="text-gray-600">Nom complet</Label>
                  <p className="font-medium">{demande.collaborateur_nom}</p>
                </div>
                <div>
                  <Label className="text-gray-600">Matricule</Label>
                  <p className="font-medium">{demande.collaborateur_matricule}</p>
                </div>
                {demande.collaborateur?.secteur && (
                  <div>
                    <Label className="text-gray-600">Secteur</Label>
                    <p className="font-medium">{demande.collaborateur.secteur}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Type d'EPI */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Type d'EPI
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <Label className="text-gray-600">Nom</Label>
                  <p className="font-medium">{demande.type_epi_nom}</p>
                </div>
                {demande.type_epi?.description && (
                  <div>
                    <Label className="text-gray-600">Description</Label>
                    <p className="text-sm text-gray-700">{demande.type_epi.description}</p>
                  </div>
                )}
                {demande.type_epi?.duree_validite_mois && (
                  <div>
                    <Label className="text-gray-600">Durée de validité</Label>
                    <p className="font-medium">{demande.type_epi.duree_validite_mois} mois</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Commentaires */}
          <Card>
            <CardHeader>
              <CardTitle>Commentaires</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-gray-600">Commentaire de la demande</Label>
                <p className="text-sm text-gray-700 mt-1">
                  {demande.commentaire || <em>Aucun commentaire</em>}
                </p>
              </div>
              {demande.commentaire_hse && (
                <div>
                  <Label className="text-gray-600">Commentaire HSE</Label>
                  <p className="text-sm text-gray-700 mt-1">{demande.commentaire_hse}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents confidentiels */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Documents joints
                <Badge className="bg-amber-100 text-amber-800 gap-1 ml-2">
                  <Lock className="h-3 w-3" />
                  Confidentiel
                </Badge>
              </CardTitle>
              <CardDescription>
                Documents accessibles uniquement aux Managers, HSE et RH
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!canViewDocuments ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg">
                  <Lock className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500 text-sm">
                    Accès réservé aux Managers, HSE et RH
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Upload de documents */}
                  {canUploadDocuments && (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="document-description">Description (optionnelle)</Label>
                          <Input
                            id="document-description"
                            placeholder="Ex: Prescription médicale, Certificat..."
                            value={documentDescription}
                            onChange={(e) => setDocumentDescription(e.target.value)}
                            className="mt-1"
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileUpload}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                            className="hidden"
                            id="file-upload"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="gap-2"
                          >
                            <Upload className="h-4 w-4" />
                            {uploading ? 'Upload en cours...' : 'Ajouter un document'}
                          </Button>
                          <span className="text-xs text-gray-500">
                            PDF, Images, Word, Excel (max 10 MB)
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Liste des documents */}
                  {documents.length === 0 ? (
                    <div className="text-center py-4">
                      <FileText className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                      <p className="text-gray-500 text-sm">Aucun document joint</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{doc.filename}</p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(doc.file_size)} • {doc.uploaded_by_name} • {formatDate(doc.uploaded_at)}
                              </p>
                              {doc.description && (
                                <p className="text-xs text-gray-600 italic mt-1">{doc.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleViewDocument(doc.id)}
                              title="Visualiser"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                              title="Télécharger"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            {canDeleteDocument(doc) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteDocument(doc.id)}
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Modification HSE */}
          {isHSE && editMode && (
            <Card>
              <CardHeader>
                <CardTitle>Modifier la demande</CardTitle>
                <CardDescription>Mettre à jour le statut et les informations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select
                    value={updateData.statut}
                    onValueChange={(value) => setUpdateData({ ...updateData, statut: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="En attente">En attente</SelectItem>
                      <SelectItem value="En cours">En cours</SelectItem>
                      <SelectItem value="Livrée">Livrée</SelectItem>
                      <SelectItem value="Rejetée">Rejetée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {updateData.statut === 'Livrée' && (
                  <div className="space-y-2">
                    <Label>Date de livraison</Label>
                    <Input
                      type="date"
                      value={updateData.date_livraison}
                      onChange={(e) => setUpdateData({ ...updateData, date_livraison: e.target.value })}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Commentaire HSE</Label>
                  <Textarea
                    value={updateData.commentaire_hse}
                    onChange={(e) => setUpdateData({ ...updateData, commentaire_hse: e.target.value })}
                    rows={3}
                    placeholder="Informations sur le traitement..."
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleUpdate}
                    disabled={updating}
                    className="flex-1 gap-2"
                  >
                    <Save className="h-4 w-4" />
                    {updating ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                  <Button
                    onClick={() => setEditMode(false)}
                    variant="outline"
                    disabled={updating}
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Historique */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Historique
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label className="text-gray-600">Demandé par</Label>
                <p className="text-sm font-medium">{demande.manager_nom}</p>
                <p className="text-xs text-gray-500">{formatDate(demande.date_demande)}</p>
              </div>
              {demande.date_traitement && (
                <div className="space-y-1">
                  <Label className="text-gray-600">Traité par</Label>
                  <p className="text-sm font-medium">{demande.hse_nom}</p>
                  <p className="text-xs text-gray-500">{formatDate(demande.date_traitement)}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Accès documents info */}
          {canViewDocuments && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <Eye className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Accès documents</p>
                    <p className="text-xs text-amber-700 mt-1">
                      En tant que {userRole}, vous avez accès aux documents confidentiels de cette demande.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemandeEPIDetail;
